import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Sticker, Loader2, Plus, Car, Baby, Dog, Briefcase, Star, CreditCard, ShieldCheck, Clock } from "lucide-react";
import AppShell from "../components/app/AppShell";
import { Button } from "../components/ui/button";
import { useI18n } from "../i18n";
import { protectionState } from "./Protections";

/**
 * RC-WEB-6 PHASE 9 — Sticker Studio as a real creation workflow.
 * A pending profile no longer looks like a broken QR card: its primary action
 * is `Activar protección`, which enters the ONE canonical checkout with the
 * correct profile id.
 */

const TYPE_ICON = { vehicle: Car, kid: Baby, pet: Dog, object: Briefcase, other: Star };
const TYPE_TINT = {
  vehicle: "bg-[#1B84FF]/10 text-[#1B84FF]",
  kid: "bg-rose-100 text-rose-600",
  pet: "bg-amber-100 text-amber-700",
  object: "bg-indigo-100 text-indigo-600",
  other: "bg-teal-100 text-teal-700",
};

const StickerStudio = () => {
  const { t } = useI18n();
  const [protections, setProtections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get("/api/vehicles")
      .then((r) => { if (alive) setProtections(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <AppShell
      title={t("app.sticker.title")}
      subtitle={t("app.sticker.subtitle")}
      maxWidth="max-w-5xl"
      backTo="/dashboard/vehicles"
    >
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#1B84FF]" /></div>
      ) : protections.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="sticker-studio-empty">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#1B84FF]/10">
            <Sticker className="h-8 w-8 text-[#1B84FF]" />
          </div>
          <h3 className="mb-2 text-lg font-bold text-[#0B2545]">{t("app.sticker.empty_title")}</h3>
          <p className="mx-auto mb-6 max-w-md text-sm text-slate-600">{t("app.sticker.empty_text")}</p>
          <Link to="/new-protection">
            <Button className="rounded-full bg-[#1B84FF] font-semibold text-white hover:bg-[#0F6FDB]" data-testid="sticker-studio-new-btn">
              <Plus className="mr-2 h-4 w-4" /> {t("app.protections.new")}
            </Button>
          </Link>
        </div>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="sticker-studio-list">
          {protections.map((p) => {
            const type = p.protection_type || "vehicle";
            const Icon = TYPE_ICON[type] || Car;
            const state = protectionState(p);
            const active = state === "active";
            return (
              <li
                key={p.id}
                className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg"
                data-testid={`sticker-card-${p.id}`}
              >
                <div className="flex items-start gap-3">
                  <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${TYPE_TINT[type] || TYPE_TINT.other}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-bold text-[#0B2545]">{p.name}</p>
                    <p className="text-xs text-slate-600">{t(`app.protections.type_${type}`)}</p>
                  </div>
                </div>

                <div className="mt-4">
                  {active ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold text-emerald-800">
                      <ShieldCheck className="h-3 w-3" /> {t("app.protections.status_active")}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-[11px] font-bold text-amber-900"
                          data-testid={`sticker-pending-${p.id}`}>
                      <Clock className="h-3 w-3" /> {t("app.sticker.pending_label")}
                    </span>
                  )}
                </div>

                {!active && (
                  <p className="mt-2 text-xs text-slate-600" data-testid={`sticker-pending-text-${p.id}`}>
                    {t("app.sticker.pending_text")}
                  </p>
                )}

                <div className="mt-5 flex flex-col gap-2">
                  {active ? (
                    <Link to={`/vehicle/${p.id}?tab=sticker`}>
                      <Button
                        className="w-full rounded-full bg-[#1B84FF] font-semibold text-white hover:bg-[#0F6FDB]"
                        data-testid={`sticker-studio-open-${p.id}`}
                      >
                        <Sticker className="mr-2 h-4 w-4" /> {t("app.sticker.open")}
                      </Button>
                    </Link>
                  ) : (
                    <>
                      <Link to={`/checkout/${p.id}`}>
                        <Button
                          className="w-full rounded-full bg-[#1B84FF] font-semibold text-white hover:bg-[#0F6FDB]"
                          data-testid={`sticker-activate-${p.id}`}
                        >
                          <CreditCard className="mr-2 h-4 w-4" /> {t("app.sticker.activate")}
                        </Button>
                      </Link>
                      <Link to={`/vehicle/${p.id}?tab=sticker`}>
                        <Button
                          variant="outline"
                          className="w-full rounded-full border-slate-300 font-semibold text-[#0B2545]"
                          data-testid={`sticker-studio-open-${p.id}`}
                        >
                          <Sticker className="mr-2 h-4 w-4" /> {t("app.sticker.open")}
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </AppShell>
  );
};

export default StickerStudio;
